import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../hive/ui/Icon';
import { useFairTracker } from '../data/FairTrackerStore';
import { Empty, useToast } from '../ui';
import { revisitList, samplesToCollect, supplierSort, digits } from '../rules';
import { dateLabel } from '../rules/format';
import { p } from '../routes';
import { tr } from '../i18n';

/**
 * Booths to Revisit (spec § 5.8) — the walking list for the next morning.
 *
 * Three sections, in the order the buyer needs them: Appointments first
 * (time-bound), then samples to physically collect, then the suppliers to walk
 * back to, sorted by rule R1 (by company, numeric-aware).
 */
export default function RevisitScreen() {
  const toast = useToast();
  const { fair, visibleSuppliers, saveSupplier } = useFairTracker();

  const appointments = visibleSuppliers
    .flatMap((s) =>
      (s.followUps || [])
        .filter((f) => f.type === 'Appointment' && f.status !== 'Done')
        .map((f) => ({ supplier: s, followUp: f })),
    )
    .sort((a, b) =>
      `${a.followUp.date || '9999'}${a.followUp.time || ''}`.localeCompare(
        `${b.followUp.date || '9999'}${b.followUp.time || ''}`,
      ),
    );

  const samples = samplesToCollect(visibleSuppliers);
  const booths = revisitList(visibleSuppliers);

  /* Ticking an appointment marks it Done. */
  const closeAppointment = async (supplier, followUp) => {
    try {
      await saveSupplier({
        ...supplier,
        followUps: (supplier.followUps || []).map((f) =>
          f.id === followUp.id ? { ...f, status: 'Done' } : f,
        ),
      });
      toast('Appointment done');
    } catch (e) {
      toast('Could not save', 'error');
    }
  };

  /* Ticking a sample sets sample = Received (rule R2's exit condition). */
  const collectSample = async (supplier, product) => {
    try {
      await saveSupplier({
        ...supplier,
        products: (supplier.products || []).map((x) =>
          x.id === product.id ? { ...x, sample: 'Received' } : x,
        ),
      });
      toast('Sample collected');
    } catch (e) {
      toast('Could not save', 'error');
    }
  };

  /* Ticking a booth clears the revisit flag. */
  const clearRevisit = async (supplier, product) => {
    try {
      await saveSupplier({
        ...supplier,
        products: (supplier.products || []).map((x) =>
          x.id === product.id ? { ...x, revisit: false } : x,
        ),
      });
      toast('Cleared');
    } catch (e) {
      toast('Could not save', 'error');
    }
  };

  const nothing = !appointments.length && !samples.length && !booths.length;

  return (
    <div className="ft-page">
      <div className="ft-title">
        <h1 className="ft-h1">{tr("Booths to Revisit")}</h1>
        <p className="ft-sub" style={{ margin: 0 }}>
          {fair ? fair.name : ''} · {tr('everything you said you would go back for')}
        </p>
      </div>

      {nothing && (
        <Empty title={tr("Nothing to go back for")}>
          Tick &quot;Revisit this booth&quot; in Quick Entry and it appears here.
        </Empty>
      )}

      {appointments.length > 0 && (
        <section>
          <h2 className="ft-h2 ft-sectionhead">{tr("Appointments")}</h2>
          <div className="ft-list ft-list-rows">
            {appointments.map(({ supplier, followUp }) => (
              <div className="ft-rowcard" key={followUp.id}>
                <input
                  type="checkbox"
                  aria-label="Mark done"
                  onChange={() => closeAppointment(supplier, followUp)}
                />
                <div className="ft-rowcard-main">
                  <b>{supplier.company}</b>
                  <div className="ft-faint">
                    {[dateLabel(followUp.date), followUp.time, followUp.notes].filter(Boolean).join(' · ')}
                  </div>
                </div>
                <Link className="ft-btn ft-btn-sm" to={p(`/s/${supplier.id}`)}>
                  {tr("Open")}
                </Link>
              </div>
            ))}
          </div>
        </section>
      )}

      {samples.length > 0 && (
        <section>
          <h2 className="ft-h2 ft-sectionhead">{tr("Samples to collect")}</h2>
          <div className="ft-list ft-list-rows">
            {samples.map(({ supplier, product }) => (
              <div className="ft-rowcard" key={`${supplier.id}-${product.id}`}>
                <input
                  type="checkbox"
                  aria-label="Mark collected"
                  onChange={() => collectSample(supplier, product)}
                />
                <div className="ft-rowcard-main">
                  <b>{supplier.company}</b>
                  <div className="ft-faint">
                    {product.name}
                    {product.samplePickup ? ` · pick up ${dateLabel(product.samplePickup)}` : ''}
                    {product.sampleHandover ? ` · ${product.sampleHandover}` : ''}
                  </div>
                </div>
                <Link className="ft-btn ft-btn-sm" to={p(`/s/${supplier.id}`)}>
                  {tr("Open")}
                </Link>
              </div>
            ))}
          </div>
        </section>
      )}

      {booths.length > 0 && (
        <section>
          <h2 className="ft-h2 ft-sectionhead">{tr("Booths")}</h2>
          <div className="ft-list ft-list-rows">
            {[...booths]
              .sort((a, b) => supplierSort(a.supplier, b.supplier))
              .map(({ supplier, product }) => {
                const phone = digits(
                  (supplier.contacts || []).map((c) => c.phone).find(Boolean) || '',
                );
                return (
                  <div className="ft-rowcard" key={`${supplier.id}-${product.id}`}>
                    <input
                      type="checkbox"
                      aria-label="Clear revisit"
                      onChange={() => clearRevisit(supplier, product)}
                    />
                    <div className="ft-rowcard-main">
                      <b>{supplier.company}</b>
                      <div className="ft-faint">
                        {product.name}
                        {product.revisitReason ? ` — ${product.revisitReason}` : ''}
                      </div>
                    </div>
                    <Link className="ft-btn ft-btn-sm" to={p(`/s/${supplier.id}`)}>
                      {tr("Open")}
                    </Link>
                    {phone && (
                      <a className="ft-btn ft-btn-sm" href={`tel:${phone}`} aria-label={tr("Call")}>
                        <Icon name="Phone" size={15} />
                      </a>
                    )}
                  </div>
                );
              })}
          </div>
        </section>
      )}
    </div>
  );
}
